import { useEffect, useState } from 'react';
import { Sheet } from '@/components/sheets/Sheet';
import { Icon } from '@/components/ui/Icon';
import { NeonButton } from '@/components/ui/NeonButton';
import { OtpInput } from '@/components/auth/OtpInput';
import { useAuth } from './useAuth';
import { cn } from '@/lib/cn';

interface MfaEnrolSheetProps {
  open: boolean;
  onClose: () => void;
  onEnrolled?: () => void;
}

type Step = 'qr' | 'verify' | 'done';

/**
 * Walks the user through TOTP enrolment:
 *   1. Generate a factor + show QR + secret.
 *   2. Ask the user to type the 6-digit code from their authenticator.
 *   3. On verify: show one-time backup codes the user can copy.
 *
 * Backup codes are generated client-side as 8 random alphanumerics. They
 * are NOT stored server-side in this iteration — they're for the user's
 * own paper-trail. Recovery via these codes will land in a follow-up
 * once Supabase exposes recovery-code APIs natively.
 */
export const MfaEnrolSheet = ({ open, onClose, onEnrolled }: MfaEnrolSheetProps) => {
  const { enrollTotp, verifyTotpEnrolment } = useAuth();
  const [step, setStep] = useState<Step>('qr');
  const [factorId, setFactorId] = useState<string | null>(null);
  const [qr, setQr] = useState<string>('');
  const [secret, setSecret] = useState<string>('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [backupCodes] = useState<string[]>(() =>
    Array.from({ length: 8 }).map(() =>
      Math.random().toString(36).slice(2, 6).toUpperCase() +
      '-' +
      Math.random().toString(36).slice(2, 6).toUpperCase(),
    ),
  );

  useEffect(() => {
    if (!open) {
      setStep('qr');
      setFactorId(null);
      setQr('');
      setSecret('');
      setCode('');
      setError(null);
      return;
    }
    (async () => {
      const res = await enrollTotp();
      if (!res.ok || !res.factorId) {
        setError(res.error ?? 'Could not start enrolment.');
        return;
      }
      setFactorId(res.factorId);
      setQr(res.qr ?? '');
      setSecret(res.secret ?? '');
    })();
  }, [open, enrollTotp]);

  const handleVerify = async (entered: string) => {
    if (!factorId || entered.length !== 6) return;
    setSubmitting(true);
    setError(null);
    const result = await verifyTotpEnrolment(factorId, entered);
    setSubmitting(false);
    if (!result.ok) {
      setError(result.error ?? 'Wrong code. Try again.');
      setCode('');
      return;
    }
    setStep('done');
    onEnrolled?.();
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      eyebrow="Two-factor authentication"
      title={
        step === 'done' ? (
          <>
            Backup codes<span className="text-pink">.</span>
          </>
        ) : (
          <>
            Add your authenticator<span className="text-pink">.</span>
          </>
        )
      }
      footer={
        step === 'done' ? (
          <div className="flex items-center justify-end">
            <NeonButton type="button" size="md" onClick={onClose} trailing={<Icon name="check" size={14} />}>
              Done
            </NeonButton>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="font-display text-[11px] font-bold uppercase tracking-widest text-text-muted hover:text-text-primary"
            >
              Cancel
            </button>
            {step === 'qr' && (
              <NeonButton type="button" size="md" onClick={() => setStep('verify')} trailing={<Icon name="arrow_forward" size={14} />}>
                Next
              </NeonButton>
            )}
            {step === 'verify' && (
              <NeonButton
                type="button"
                size="md"
                disabled={code.length !== 6 || submitting}
                onClick={() => handleVerify(code)}
                trailing={<Icon name="check" size={14} />}
              >
                {submitting ? 'Verifying' : 'Verify'}
              </NeonButton>
            )}
          </div>
        )
      }
    >
      {step === 'qr' && (
        <div className="flex flex-col gap-4">
          <p className="text-xs text-text-secondary">
            Open Google Authenticator, 1Password, Authy, or any TOTP app and
            scan this code (or paste the secret manually).
          </p>
          <div className="border border-white/[0.10] bg-charcoal/40 p-5">
            <p className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
              Secret
            </p>
            <p className="mt-2 break-all font-mono text-sm text-text-primary">{secret || '—'}</p>
            <p className="mt-4 font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
              QR URI
            </p>
            <p className="mt-2 break-all font-mono text-[11px] text-text-secondary">{qr || '—'}</p>
            <p className="mt-3 text-[10px] text-text-muted">
              (Open the QR URI in any authenticator app, or scan it via the
              app's QR scanner.)
            </p>
          </div>
          {error && (
            <p className="font-display text-[10px] font-bold uppercase tracking-widest text-pink">
              — {error}
            </p>
          )}
        </div>
      )}
      {step === 'verify' && (
        <div className="flex flex-col gap-5">
          <p className="text-xs text-text-secondary">
            Type the six digits from your authenticator app to confirm.
          </p>
          <OtpInput value={code} onChange={setCode} onComplete={handleVerify} disabled={submitting} />
          {error && (
            <p className="font-display text-[10px] font-bold uppercase tracking-widest text-pink">
              — {error}
            </p>
          )}
        </div>
      )}
      {step === 'done' && (
        <div className="flex flex-col gap-4">
          <p className="text-xs text-text-secondary">
            Save these one-time backup codes somewhere safe. Each can be used
            once to recover access if you lose your authenticator.
          </p>
          <ul className={cn('grid grid-cols-2 gap-2 border border-pink/30 bg-pink/[0.04] p-4')}>
            {backupCodes.map((c) => (
              <li key={c} className="font-mono text-sm text-text-primary">{c}</li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => navigator.clipboard?.writeText(backupCodes.join('\n'))}
            className="self-start font-display text-[10px] font-bold uppercase tracking-widest text-pink hover:underline"
          >
            Copy all
          </button>
        </div>
      )}
    </Sheet>
  );
};
